import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { maintenanceDayQuery, type CalendarWindow } from '../api/maintenance'
import { ApiError } from '../api/client'
import { moduleOf, shellQuery } from '../api/shell'
import { CmdbViews } from '../cmdb/CmdbViews'
import { AdmPageHead } from '../softsee/adm/AdmPageHead'
import { Forbidden } from '../ui/Forbidden'
import { toast } from '../ui/notify'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'
import { pairs } from './MaintenanceCalendarPage'
import { MaintenanceShapes, SCOPE_ICON } from './MaintenanceShapes'
import { WindowFormModal } from './WindowFormModal'

/**
 * `Maintenance::day` - o zi, ora cu ora, la /app/maintenance/day?date=.
 *
 * Luna raspunde "care zi", si se opreste acolo. Aici ziua e o rigla si fiecare
 * fereastra o bara pe ea; pentru fiecare ciocnire se numesc orele pe care cele
 * doua chiar le impart. `views/version-3/inventoree/maintenance/day.php`.
 */

const STYLES = ['css/adm.css']

function ts(value: string): number {
  return new Date(value.replace(' ', 'T')).getTime()
}

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00`)
  d.setDate(d.getDate() + n)
  const pad = (x: number) => String(x).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function hm(t: number): string {
  const d = new Date(t)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

export function MaintenanceDayPage() {
  const shell = useQuery(shellQuery(moduleOf('/maintenance')))
  usePageStyles(STYLES, shell.data?.version ?? '')

  const date = new URLSearchParams(window.location.search).get('date') ?? ''
  const page = useQuery(maintenanceDayQuery(date))
  const [form, setForm] = useState<{ id: number; hour: number | null } | null>(null)

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const data = page.data
  const dayStart = ts(`${data.date} 00:00:00`)
  const span = 24 * 3600 * 1000
  const today = new Date().toISOString().slice(0, 10)
  const byId = new Map(data.windows.map((w) => [w.ID, w]))

  const place = (w: CalendarWindow) => {
    const from = Math.max(ts(w.starts_at_full), dayStart)
    const to = Math.min(ts(w.ends_at_full), dayStart + span)
    return {
      left: ((from - dayStart) / span) * 100,
      width: Math.max(0.5, ((to - from) / span) * 100),
      before: ts(w.starts_at_full) < dayStart,
      after: ts(w.ends_at_full) > dayStart + span,
    }
  }

  const clashes = pairs(data.windows, data.conflicts).map((pair) => {
    const other = byId.get(pair.other.id)
    let shared: { from: number; to: number } | null = null
    if (other) {
      const from = Math.max(ts(pair.one.starts_at_full), ts(other.starts_at_full), dayStart)
      const to = Math.min(ts(pair.one.ends_at_full), ts(other.ends_at_full), dayStart + span)
      if (to > from) shared = { from, to }
    }
    return { ...pair, shared }
  })

  return (
    <Content>
      {/* Banda celor patru vederi, primul copil al invelisului - vezi
          `MaintenanceWindowsPage`, unde sta scris de ce nu dupa cartonas. */}
      <CmdbViews views={data.views} current="maintenance" />

      <AdmPageHead
        tone="green-mode"
        icon="fa-clock"
        title="Change calendar"
        note={new Date(`${data.date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        stats={[
          { value: String(data.windows.length), label: 'open during this day', tone: 'grey' },
          { value: String(clashes.length), label: 'clashes', tone: clashes.length ? 'red-intense' : 'grey' },
        ]}
        actions={
          <>
            {data.canWrite && (
              <button type="button" className="btn btn-primary btn-sm" id="windowAdd" onClick={() => setForm({ id: 0, hour: null })}>
                <i className="fas fa-plus-circle"></i> Add a window
              </button>
            )}{' '}
            <a className="btn btn-default btn-sm" href="/app/cmdb/health#admChanges">
              <i className="fas fa-user-shield"></i> Unauthorised changes
            </a>
          </>
        }
      />

      <div className="adm-bar">
        <div className="adm-field">
          <label>Day</label>
          <div>
            <span className="btn-group">
              <a className="btn btn-sm btn-default" href={`/app/maintenance/day?date=${addDays(data.date, -1)}`} title="Previous day">
                <i className="fas fa-chevron-left"></i>
              </a>
              <a className="btn btn-sm btn-default" href={`/app/maintenance/day?date=${addDays(data.date, 1)}`} title="Next day">
                <i className="fas fa-chevron-right"></i>
              </a>
            </span>{' '}
            <strong>{data.date}</strong>
          </div>
        </div>
        <div className="adm-spacer"></div>
        <div className="adm-field">
          <label>Shape</label>
          <MaintenanceShapes active="day" dayDate={data.date} size="sm" />
        </div>
      </div>

      <div className="adm-section adm-section-green-mode">
        <h4>
          <i className="fas fa-clock"></i> {data.date}
        </h4>
        <span className="adm-section-note">
          {data.canWrite ? 'click an empty hour to plan a window that starts there' : 'every window open at any point during this day'}
        </span>
      </div>

      {clashes.length > 0 && (
        <div className="adm-card adm-tone-red-intense">
          <div className="adm-card-head">
            <h5>
              <i className="fas fa-exclamation-triangle"></i> {clashes.length} clash{clashes.length === 1 ? '' : 'es'}
            </h5>
            <span className="adm-card-note">the hours each pair actually shares</span>
          </div>
          <table className="table adm-list">
            <tbody>
              {clashes.map((pair) => (
                <tr key={`${pair.one.ID}-${pair.other.id}`}>
                  <td>
                    <strong>{pair.one.name}</strong>
                    <span className="adm-muted">&nbsp;&times;&nbsp;</span>
                    <strong>{pair.other.name}</strong>
                    <div className="adm-muted" style={{ fontSize: 11 }}>
                      {pair.other.reason}
                    </div>
                  </td>
                  <td className="adm-nowrap" style={{ width: 190 }}>
                    {pair.shared ? (
                      <>
                        <span className="adm-pill adm-pill-bad">
                          {hm(pair.shared.from)} &ndash; {hm(pair.shared.to)}
                        </span>
                        <div className="adm-muted" style={{ fontSize: 11 }}>
                          {Math.round((pair.shared.to - pair.shared.from) / 60000)} minutes together
                        </div>
                      </>
                    ) : (
                      <span className="adm-muted">on another day</span>
                    )}
                  </td>
                  {data.canWrite && (
                    <td className="text-right adm-nowrap" style={{ width: 90 }}>
                      <button className="btn btn-default btn-xs windowEdit" onClick={() => setForm({ id: pair.one.ID, hour: null })}>
                        <i className="fa fa-pencil-alt"></i> Move
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.windows.length === 0 ? (
        <div className="adm-empty-inline">
          <i className="fas fa-clock"></i> Nothing planned for this day. {data.canWrite ? 'Add a window, or move to a day that has one.' : ''}
        </div>
      ) : (
        <div className="adm-daygrid">
          <div className="adm-daygrid-head">
            <span className="adm-daygrid-label"></span>
            <span className="adm-daygrid-ruler">
              {Array.from({ length: 24 }, (_, h) => (
                <span key={h} className="adm-hour">
                  {String(h).padStart(2, '0')}
                </span>
              ))}
            </span>
          </div>
          {data.windows.map((w) => {
            const at = place(w)
            const cl = data.conflicts[String(w.ID)] ?? []
            return (
              <div className="adm-daygrid-row" key={w.ID}>
                <span className="adm-daygrid-label" title={`${w.name} — ${w.scope_name}`}>
                  <i className={`fas ${SCOPE_ICON[w.scope_type] ?? 'fa-cube'} adm-muted`}></i> {w.name} <em>{w.scope_name}</em>
                </span>
                <span
                  className="adm-daygrid-lane"
                  data-day={data.canWrite ? data.date : undefined}
                  onClick={(e) => {
                    if (!data.canWrite || (e.target as HTMLElement).closest('.adm-daybar')) return
                    const rect = e.currentTarget.getBoundingClientRect()
                    setForm({ id: 0, hour: Math.min(23, Math.max(0, Math.floor(((e.clientX - rect.left) / rect.width) * 24))) })
                  }}
                >
                  {data.date === today && <span className="adm-now" title="now" style={{ left: `${((Date.now() - dayStart) / span) * 100}%` }}></span>}
                  <span
                    className={`adm-daybar adm-daybar-${w.state} ${cl.length ? 'adm-daybar-clash' : ''}`}
                    style={{ left: `${at.left}%`, width: `${at.width}%` }}
                    data-id={w.ID}
                    title={`${w.starts_at} → ${w.ends_at}${w.ticket_id ? ` — ticket #${w.ticket_id}` : ''}`}
                    onClick={() => data.canWrite && setForm({ id: w.ID, hour: null })}
                  >
                    {at.before && <i className="fas fa-caret-left"></i>} {w.starts_at_full.slice(11, 16)}
                    {at.after && <i className="fas fa-caret-right"></i>}
                  </span>
                </span>
              </div>
            )
          })}
        </div>
      )}

      <div className="adm-calendar-legend">
        <span>
          <i className="adm-key adm-key-upcoming"></i> Upcoming
        </span>
        <span>
          <i className="adm-key adm-key-open"></i> Open now
        </span>
        <span>
          <i className="adm-key adm-key-past"></i> Finished
        </span>
        <span>
          <i className="adm-key adm-key-clash"></i> Could reach the same item as another
        </span>
      </div>

      <WindowFormModal
        open={form !== null}
        id={form?.id ?? 0}
        day={form !== null && form.id === 0 ? data.date : ''}
        hour={form?.hour ?? null}
        onClose={() => setForm(null)}
        onSaved={(message, warning) => toast(message, 'Maintenance windows', warning ? 'warning' : 'success')}
      />
    </Content>
  )
}
